import React, { ReactElement, ReactNode } from 'react';

import { useScreenMediaQuery } from '@shared/hooks';

import { useMerchantPermissions } from '@/hooks/useMerchantPermissions';
import { Button } from '@/widgets';
import { ArrowLeftIcon, StarIcon } from '@/assets/icons';
import { sendClickEvent } from '@/utils/AnalyticsUtils';
import { TOUR_STARTED } from '@/constants';
import { TEST_IDS } from '@/constants/testConstants';
import { RouteNameType } from '@/types';

import styles from './PageHeader.module.scss';
import MerchantListSelect from '../merchantList/MerchantListSelect';
import { IMerchantListSelectProps } from '../merchantList/merchantList.types';
import NotificationsWidget from '../notification/NotificationsWidget';

/**
 * @description UI Component to display the header on page.
 *
 * @param {PageHeaderProps}  props
 *
 * @returns {React.Element}
 */

export interface IPageHeaderProps {
	pageTitle: string;
	pageDescription?: string;
	handleBackIconClick?: () => void;
	RhsComponent?: ReactNode;
	showNotificationIcon?: boolean;
	showSuperMerchantSelect?: boolean;
	merchantSelectMode?: IMerchantListSelectProps['mode'];
	isPageLoading?: boolean;
	showCoachmarkButton?: boolean;
	handleCoachmarkClick?: () => void;
	routeName?: RouteNameType;
}
const { PAGE_HEADING } = TEST_IDS;

const PageHeader = (props: IPageHeaderProps): ReactElement => {
	const {
		pageTitle,
		pageDescription = '',
		handleBackIconClick = null,
		RhsComponent = null,
		showNotificationIcon = true,
		showSuperMerchantSelect = true,
		merchantSelectMode = 'multiple',
		isPageLoading = false,
		showCoachmarkButton = false,
		handleCoachmarkClick = null,
		routeName = '',
	} = props;
	const { isSuperMerchant } = useMerchantPermissions();
	const isTablet = useScreenMediaQuery('maxTablet');

	const startTour = () => {
		sendClickEvent({
			name: TOUR_STARTED,
			value: window.location.pathname,
		});
		handleCoachmarkClick?.();
	};

	return (
		<div className={styles.container}>
			<div className={styles.header}>
				<div className={styles.headerLhs}>
					{handleBackIconClick && (
						<ArrowLeftIcon
							onClick={handleBackIconClick}
							className={styles.backIcon}
							data-id={TEST_IDS.PAGE_HEADER.GO_BACK}
						/>
					)}
					<div className={styles.pageTitle} data-id={PAGE_HEADING}>
						{pageTitle}
					</div>
					{!isTablet && showCoachmarkButton && (
						<Button
							className={styles.coachmarkButton}
							variant="tertiary"
							onClick={startTour}
							data-id={TEST_IDS.COACH_MARK.TAKE_TOUR}
						>
							<StarIcon
								className={styles.coachmarkIcon}
								width={20}
								height={20}
							/>
							<span>Take a Tour</span>
						</Button>
					)}
				</div>
				<div className={styles.headerRhs}>
					{RhsComponent}
					{isSuperMerchant && showSuperMerchantSelect && routeName && (
						<MerchantListSelect
							mode={merchantSelectMode}
							loading={isPageLoading}
							routeName={routeName}
						/>
					)}
					<NotificationsWidget showNotificationIcon={showNotificationIcon} />
				</div>
			</div>
			<div className={styles.pageDescription}>{pageDescription}</div>
		</div>
	);
};

export default PageHeader;
